// Screen — Agencies (the dedicated agency surface).
//
// Full editing over /admin/agencies: the table (name, status, fee %, telegram
// contact, BC id, chat-binding badge, created), a New-agency modal, and a
// row-click edit drawer with ALL fields editable. The CRM page keeps its
// read-only agencies tab; THIS page is where agencies are edited.
//
// CONTRACT: PUT /admin/agencies/{id} replaces ALL fields (regression-pinned
// server-side) — the drawer always sends the COMPLETE body derived from the
// fetched record, so untouched fields round-trip instead of being cleared.
//
// BC id: a manual input, PLUS a dropdown fed by GET /admin/tiktok/bcs when
// that endpoint is available. On 404 (not deployed) / 503 (master-token client
// not wired) / 502 (upstream error) the page gracefully degrades to
// input-only.
//
// Chat binding: the badge column resolves bound/unbound from GET
// /admin/ops-chats (tolerant — a failed lookup renders "—" and hands the
// drawer's embedded TelegramChatBinding its own self-load). Binding writes go
// only through the authorized admin endpoints; the bot token never reaches
// the browser.

import { useCallback, useEffect, useId, useState } from "react";
import { toast } from "sonner";

import {
  agencyToBody,
  createAgency,
  EMPTY_AGENCY,
  listAgencies,
  updateAgency,
  type AgencyBody,
  type AgencyRecord,
} from "@/api/agencies";
import { listBCs, type BCOption } from "@/api/tiktok";
import { listOpsChats, type ChatBinding } from "@/api/telegram";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import { TelegramChatBinding } from "@/components/TelegramChatBinding";
import {
  DataTable,
  Drawer,
  Modal,
  StatusPill,
  ToneBadge,
  type Column,
  type Tone,
} from "@/components/nexus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { NativeSelect } from "./controls";

const LABEL_CLASS =
  "mb-1 block text-xs font-medium tracking-wide text-nx-text-2 uppercase";

// Status is free TEXT server-side; these are the conventional values. The
// edit select injects the row's current value when it is not one of them, so
// an unknown status is never silently rewritten.
const STATUS_OPTIONS = ["active", "paused", "disabled", "banned"] as const;

function statusTone(status: string): Tone {
  switch (status) {
    case "active":
      return "credit";
    case "paused":
      return "warn";
    case "disabled":
    case "banned":
      return "danger";
    default:
      return "na";
  }
}

function fmtCreated(unixSeconds: number): string {
  if (!unixSeconds) return "—";
  return new Date(unixSeconds * 1000).toISOString().slice(0, 10);
}

// The BC-id field: always a manual input; a picker <select> appears when the
// bcs endpoint answered (choosing an option fills the input — the input stays
// the source of truth). bcs === null → endpoint unavailable → input-only.
function BCIdField({
  value,
  onChange,
  bcs,
}: {
  value: string;
  onChange: (v: string) => void;
  bcs: BCOption[] | null | undefined;
}) {
  const inputId = useId();
  return (
    <div>
      <label htmlFor={inputId} className={LABEL_CLASS}>
        BC ID
      </label>
      <div className="grid gap-1.5">
        <Input
          id={inputId}
          value={value}
          spellCheck={false}
          autoComplete="off"
          placeholder="Business Center id"
          className="font-mono"
          onChange={(e) => onChange(e.target.value)}
        />
        {bcs != null && bcs.length > 0 && (
          <NativeSelect
            aria-label="Pick a Business Center"
            value={bcs.some((b) => b.bc_id === value) ? value : ""}
            onChange={(e) => {
              if (e.target.value) onChange(e.target.value);
            }}
          >
            <option value="">Pick from Business Centers…</option>
            {bcs.map((b) => (
              <option key={b.bc_id} value={b.bc_id}>
                {b.name ? `${b.name} (${b.bc_id})` : b.bc_id}
              </option>
            ))}
          </NativeSelect>
        )}
        {bcs === null && (
          <p className="text-xs text-nx-text-3">
            Business Center list unavailable — enter the BC id manually.
          </p>
        )}
      </div>
    </div>
  );
}

// The shared text fields (create modal + edit drawer). name is REQUIRED; the
// caller renders the gate message + save button.
function AgencyTextFields({
  form,
  onPatch,
  bcs,
}: {
  form: AgencyBody;
  onPatch: (patch: Partial<AgencyBody>) => void;
  bcs: BCOption[] | null | undefined;
}) {
  const idName = useId();
  const idTelegram = useId();
  const idFee = useId();
  const idTerms = useId();
  const idScope = useId();
  const idLink = useId();
  const idNotes = useId();
  return (
    <>
      <div>
        <label htmlFor={idName} className={LABEL_CLASS}>
          Name (required)
        </label>
        <Input
          id={idName}
          value={form.name}
          aria-invalid={form.name.trim() === ""}
          placeholder="Agency name"
          onChange={(e) => onPatch({ name: e.target.value })}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={idTelegram} className={LABEL_CLASS}>
            Telegram contact
          </label>
          <Input
            id={idTelegram}
            value={form.telegram}
            spellCheck={false}
            placeholder="@handle"
            onChange={(e) => onPatch({ telegram: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor={idFee} className={LABEL_CLASS}>
            Fee %
          </label>
          <Input
            id={idFee}
            value={form.fee_pct}
            inputMode="decimal"
            placeholder="e.g. 8.5 (empty = unknown)"
            onChange={(e) => onPatch({ fee_pct: e.target.value })}
          />
        </div>
      </div>
      <div>
        <label htmlFor={idTerms} className={LABEL_CLASS}>
          Financial conditions
        </label>
        <Input
          id={idTerms}
          value={form.financial_conditions}
          placeholder="e.g. Net-15, prepaid…"
          onChange={(e) => onPatch({ financial_conditions: e.target.value })}
        />
      </div>
      <div>
        <label htmlFor={idScope} className={LABEL_CLASS}>
          Role scope
        </label>
        <Input
          id={idScope}
          value={form.role_scope}
          placeholder="e.g. full, limited"
          onChange={(e) => onPatch({ role_scope: e.target.value })}
        />
      </div>
      <div>
        <label htmlFor={idLink} className={LABEL_CLASS}>
          Cooperation link
        </label>
        <Input
          id={idLink}
          value={form.cooperation_link}
          spellCheck={false}
          placeholder="https://…"
          onChange={(e) => onPatch({ cooperation_link: e.target.value })}
        />
      </div>
      <div>
        <label htmlFor={idNotes} className={LABEL_CLASS}>
          Notes
        </label>
        <Input
          id={idNotes}
          value={form.notes}
          placeholder="Internal notes"
          onChange={(e) => onPatch({ notes: e.target.value })}
        />
      </div>
      <BCIdField
        value={form.bc_id}
        onChange={(bc_id) => onPatch({ bc_id })}
        bcs={bcs}
      />
    </>
  );
}

export function AgenciesPage() {
  const [agencies, setAgencies] = useState<AgencyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // undefined = still loading, null = endpoint unavailable (manual input only).
  const [bcs, setBcs] = useState<BCOption[] | null | undefined>(undefined);

  // agency_id -> active agency chat binding. null = the ops-chats lookup
  // failed (badge renders "—"; the drawer editor self-loads instead).
  const [chatMap, setChatMap] = useState<Record<string, ChatBinding> | null>(
    {},
  );
  const [chatsLoaded, setChatsLoaded] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AgencyRecord | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAgencies();
      setAgencies(res.agencies ?? []);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // BC options for the bc_id pickers — graceful input-only fallback.
  useEffect(() => {
    listBCs()
      .then(setBcs)
      .catch(() => setBcs(null));
  }, []);

  // Chat-binding badge data (tolerant — a failure renders "—", never blanks
  // the table).
  useEffect(() => {
    listOpsChats()
      .then((res) => {
        const map: Record<string, ChatBinding> = {};
        for (const c of res.chats ?? []) {
          if (c.active && c.kind === "agency" && c.agency_id) {
            map[c.agency_id] = c;
          }
        }
        setChatMap(map);
      })
      .catch(() => setChatMap(null))
      .finally(() => setChatsLoaded(true));
  }, []);

  const columns: Column<AgencyRecord>[] = [
    {
      key: "name",
      header: "Agency",
      cell: (r) => <span className="font-medium text-nx-text">{r.name}</span>,
    },
    {
      key: "status",
      header: "Status",
      cell: (r) =>
        r.status ? (
          <StatusPill tone={statusTone(r.status)} className="capitalize">
            {r.status}
          </StatusPill>
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "fee",
      header: "Fee %",
      cell: (r) => (
        <span className="text-nx-text-2 tabular-nums">
          {r.fee_pct ? `${r.fee_pct}%` : "—"}
        </span>
      ),
    },
    {
      key: "telegram",
      header: "Telegram",
      cell: (r) =>
        r.telegram ? (
          <span className="text-nx-text-2">{r.telegram}</span>
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "bc",
      header: "BC id",
      cell: (r) =>
        r.bc_id ? (
          <span className="font-mono text-xs text-nx-text-2">{r.bc_id}</span>
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "chat",
      header: "Chat",
      cell: (r) => {
        if (!chatsLoaded || chatMap === null) {
          return <span className="text-nx-text-3">—</span>;
        }
        return chatMap[r.id] ? (
          <ToneBadge tone="credit">bound</ToneBadge>
        ) : (
          <ToneBadge tone="na">unbound</ToneBadge>
        );
      },
    },
    {
      key: "created",
      header: "Created",
      align: "right",
      cell: (r) => (
        <span className="text-xs text-nx-text-3 tabular-nums">
          {fmtCreated(r.created_at)}
        </span>
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">Agencies</h2>
          <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
            Admin-internal agency records — click a row to edit every field and
            manage the agency's Telegram chat binding.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>New agency</Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Could not load agencies</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid gap-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={agencies}
          rowKey={(r) => r.id}
          onRowClick={setEditing}
          empty="No agencies yet."
        />
      )}

      <NewAgencyModal
        open={createOpen}
        bcs={bcs}
        onOpenChange={setCreateOpen}
        onCreated={(created) => {
          setAgencies((prev) => [...prev, created]);
          setCreateOpen(false);
        }}
      />

      <EditAgencyDrawer
        agency={editing}
        bcs={bcs}
        // Not-yet-loaded or failed lookup -> undefined lets the embedded
        // binding editor self-load its state.
        binding={
          editing && chatsLoaded && chatMap !== null
            ? (chatMap[editing.id] ?? null)
            : undefined
        }
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        onSaved={(updated) => {
          setAgencies((prev) =>
            prev.map((a) => (a.id === updated.id ? updated : a)),
          );
          setEditing(null);
        }}
        onBindingChanged={(agencyId, binding) => {
          setChatMap((prev) => {
            const next = { ...(prev ?? {}) };
            if (binding) next[agencyId] = binding;
            else delete next[agencyId];
            return next;
          });
        }}
      />
    </AdminShell>
  );
}

// ---- New-agency modal ---------------------------------------------------------

function NewAgencyModal({
  open,
  bcs,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  bcs: BCOption[] | null | undefined;
  onOpenChange: (open: boolean) => void;
  onCreated: (created: AgencyRecord) => void;
}) {
  const [form, setForm] = useState<AgencyBody>(EMPTY_AGENCY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameMissing = form.name.trim() === "";

  function reset() {
    setForm(EMPTY_AGENCY);
    setError(null);
  }

  async function save() {
    if (nameMissing || saving) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createAgency({ ...form, name: form.name.trim() });
      toast.success(`Agency "${created.name}" created`);
      reset();
      onCreated(created);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
      title="New agency"
      description="Create an admin-internal agency record. Only the name is required — everything else can be filled in later from the edit drawer."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={nameMissing || saving} onClick={save}>
            {saving ? "Creating…" : "Create agency"}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <AgencyTextFields
          form={form}
          onPatch={(patch) => setForm((f) => ({ ...f, ...patch }))}
          bcs={bcs}
        />
        {error && (
          <p
            role="alert"
            className="rounded-md bg-nx-danger-soft px-3 py-2 text-xs font-medium text-nx-danger"
          >
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ---- Edit drawer ----------------------------------------------------------------

function EditAgencyDrawer({
  agency,
  bcs,
  binding,
  onOpenChange,
  onSaved,
  onBindingChanged,
}: {
  agency: AgencyRecord | null;
  bcs: BCOption[] | null | undefined;
  // undefined = unknown (the embedded editor self-loads); null = known none.
  binding: ChatBinding | null | undefined;
  onOpenChange: (open: boolean) => void;
  onSaved: (updated: AgencyRecord) => void;
  onBindingChanged: (agencyId: string, binding: ChatBinding | null) => void;
}) {
  const [form, setForm] = useState<AgencyBody>(EMPTY_AGENCY);
  // Geos are edited as free comma-separated text and parsed on save.
  const [geosText, setGeosText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idStatus = useId();
  const idGeos = useId();

  // Re-seed the form from the record whenever a row opens.
  useEffect(() => {
    if (!agency) return;
    setForm(agencyToBody(agency));
    setGeosText((agency.geos ?? []).join(", "));
    setError(null);
  }, [agency]);

  const nameMissing = form.name.trim() === "";
  const statusOptions: string[] =
    form.status && !STATUS_OPTIONS.includes(form.status as never)
      ? [form.status, ...STATUS_OPTIONS]
      : [...STATUS_OPTIONS];

  async function save() {
    if (!agency || nameMissing || saving) return;
    setSaving(true);
    setError(null);
    try {
      // FULL body — PUT replaces all fields server-side.
      const body: AgencyBody = {
        ...form,
        name: form.name.trim(),
        geos: geosText
          .split(",")
          .map((g) => g.trim())
          .filter(Boolean),
      };
      const updated = await updateAgency(agency.id, body);
      toast.success(`Agency "${updated.name}" updated`);
      onSaved(updated);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      open={agency !== null}
      onOpenChange={onOpenChange}
      title={agency?.name}
      description="Agency · admin-internal record"
    >
      {agency && (
        <div className="grid gap-4">
          <div className="grid gap-3">
            <AgencyTextFields
              form={form}
              onPatch={(patch) => setForm((f) => ({ ...f, ...patch }))}
              bcs={bcs}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor={idStatus} className={LABEL_CLASS}>
                  Status
                </label>
                <NativeSelect
                  id={idStatus}
                  value={form.status}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, status: e.target.value }))
                  }
                >
                  <option value="">—</option>
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              <div>
                <label htmlFor={idGeos} className={LABEL_CLASS}>
                  Geos
                </label>
                <Input
                  id={idGeos}
                  value={geosText}
                  spellCheck={false}
                  placeholder="US, UK"
                  onChange={(e) => setGeosText(e.target.value)}
                />
              </div>
            </div>
          </div>

          <p className="text-xs text-nx-text-3">
            Saving replaces the whole record — clearing a field clears its
            stored value.
          </p>
          {error && (
            <p
              role="alert"
              className="rounded-md bg-nx-danger-soft px-3 py-2 text-xs font-medium text-nx-danger"
            >
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button disabled={nameMissing || saving} onClick={save}>
              {saving ? "Saving…" : "Save agency"}
            </Button>
          </div>

          {/* Telegram chat binding (validated + written entirely server-side). */}
          <div className="grid gap-2 border-t border-nx-border pt-4">
            <span className="text-xs font-medium tracking-wide text-nx-text-3 uppercase">
              Telegram chat
            </span>
            <TelegramChatBinding
              kind="agency"
              ownerId={agency.id}
              currentBinding={binding}
              onChanged={(b) => onBindingChanged(agency.id, b)}
            />
          </div>
        </div>
      )}
    </Drawer>
  );
}
